import { UXPContext } from '@core/context/Context';
import { hasValue, toDate } from '@utils';
import { format } from 'date-fns';
import { useCallback, useContext } from 'react';
export const DATE_FORMAT_MAP = {
    'Y/M/D': 'yyyy/M/d',
    'D/M/Y': 'd/M/yyyy',
    'n d, Y': 'MMM d, yyyy',
    'd-N-Y': 'd-MMMM-yyyy',
    'M/D/Y': 'M/d/yyyy',
    'D.M.Y': 'd.M.yyyy',
    'Y-M-D': 'yyyy-MM-dd'
};
export const TIME_FORMAT_MAP = {
    '12': 'h:mm a',
    '24': 'HH:mm'
};
export const DATETIME_FORMAT_MAP = {
    '12': 'h:mm:ss a',
    '24': 'HH:mm:ss'
};
export const DEFAULT_DATE_FORMAT = 'yyyy/M/d';
export const DEFAULT_TIME_FORMAT = 'HH:mm';
export const DEFAULT_DATETIME_FORMAT = 'HH:mm:ss';
/**
 * @export
 * Hook for formatting dates, times, and datetimes with timezone conversion support.
 *
 * This hook returns a `formatDate` function that you can use to format values
 * depending on the `type` you provide (`date`, `time`, or `datetime`).
 *
 * It automatically applies:
 * - System-defined formats (from context) OR your custom format strings.
 * - Timezone conversion (site, browser, or custom offset).
 * - Optional seconds in time outputs.
 *
 * @example
 * ```tsx
 * // Import the hook and get the formatDate function
 * import { useFormatters } from '@/hooks/useFormatters';
 *
 * const { formatDate } = useFormatters();
 * const now = new Date();
 * ```
 *
 * @example
 * ```tsx
 * // Format as date only
 * const date = formatDate(now, { type: 'date' });
 * ```
 *
 * @example
 * ```tsx
 * // Format as time only
 * const time = formatDate(now, { type: 'time' });
 * ```
 *
 * @example
 * ```tsx
 * // Format as date + time
 * const dateTime = formatDate(now, { type: 'datetime' });
 * ```
 *
 * @example
 * ```tsx
 * // Custom format string (date-fns format)
 * const custom = formatDate(now, { type: 'datetime', format: 'EEEE, MMMM do yyyy HH:mm' });
 * ```
 *
 * @example
 * ```tsx
 * // Include seconds in time format
 * const withSeconds = formatDate(now, { type: 'time', includeSeconds: true });
 * ```
 *
 * @example
 * ```tsx
 * // Convert using browser timezone
 * const browserTime = formatDate(now, {
 *   type: 'datetime',
 *   timezone: { useBrowserTime: true }
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Convert using a specific offset (e.g., UTC+5:30 = 19800 seconds)
 * const specificOffset = formatDate(now, {
 *   type: 'datetime',
 *   timezone: { offset: 19800 }
 * });
 * ```
 */
export const useFormatters = () => {
    const context = useContext(UXPContext);
    const getDateFormat = useCallback((systemFormat, fallback = DEFAULT_DATE_FORMAT) => {
        return systemFormat ? DATE_FORMAT_MAP[systemFormat] || fallback : fallback;
    }, []);
    const getTimeFormat = useCallback((systemFormat, fallback = DEFAULT_TIME_FORMAT, includeSeconds = false) => {
        if (!systemFormat)
            return fallback;
        return includeSeconds ? DATETIME_FORMAT_MAP[systemFormat] || fallback : TIME_FORMAT_MAP[systemFormat] || fallback;
    }, []);
    const getDateFormatFromOptions = useCallback((options) => {
        var _a;
        if (typeof (options === null || options === void 0 ? void 0 : options.format) === 'string')
            return options.format;
        return getDateFormat(((_a = options === null || options === void 0 ? void 0 : options.format) === null || _a === void 0 ? void 0 : _a.systemDateFormat) || context.userDataFormat);
    }, [getDateFormat, context.userDataFormat]);
    const getTimeFormatFromOptions = useCallback((options) => {
        var _a;
        if (typeof (options === null || options === void 0 ? void 0 : options.format) === 'string')
            return options.format;
        const fallback = (options === null || options === void 0 ? void 0 : options.includeSeconds) ? DEFAULT_DATETIME_FORMAT : DEFAULT_TIME_FORMAT;
        return getTimeFormat(((_a = options === null || options === void 0 ? void 0 : options.format) === null || _a === void 0 ? void 0 : _a.systemTimeFormat) || context.userTimeFormat, fallback, options === null || options === void 0 ? void 0 : options.includeSeconds);
    }, [getTimeFormat, context.userTimeFormat]);
    const convertToTimezone = useCallback((date, offsetSeconds) => {
        if (!offsetSeconds)
            return date;
        const offsetMs = offsetSeconds * 1000;
        const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
        return new Date(utcTime + offsetMs);
    }, []);
    const formatDT = useCallback((date, dateFnsFormat, options) => {
        var _a, _b;
        const dateObj = toDate(date);
        if (!dateObj)
            return '';
        const hasCustomOffset = hasValue((_a = options === null || options === void 0 ? void 0 : options.timezone) === null || _a === void 0 ? void 0 : _a.offset, true, true);
        if (((_b = options.timezone) === null || _b === void 0 ? void 0 : _b.useBrowserTime) && !hasCustomOffset) {
            return format(dateObj, dateFnsFormat);
        }
        const offsetSeconds = hasCustomOffset ? options.timezone.offset : context.userSiteTimeZoneOffsetInSeconds;
        const convertedDate = convertToTimezone(dateObj, offsetSeconds);
        return format(convertedDate, dateFnsFormat);
    }, [context.userSiteTimeZoneOffsetInSeconds, convertToTimezone]);
    const formatDateOnly = useCallback((date, options = {}) => {
        const dateFnsFormat = getDateFormatFromOptions(options);
        return formatDT(date, dateFnsFormat, options);
    }, [getDateFormatFromOptions, formatDT]);
    const formatTimeOnly = useCallback((date, options = {}) => {
        const dateFnsFormat = getTimeFormatFromOptions(options);
        return formatDT(date, dateFnsFormat, options);
    }, [getTimeFormatFromOptions, formatDT]);
    const formatDateTimeOnly = useCallback((date, options = {}) => {
        const format = (typeof (options === null || options === void 0 ? void 0 : options.format) == 'string')
            ? options === null || options === void 0 ? void 0 : options.format
            : `${getDateFormatFromOptions(options)} ${getTimeFormatFromOptions(options)}`;
        return formatDT(date, format, options);
    }, [getDateFormatFromOptions, getTimeFormatFromOptions, formatDT]);
    const formatDate = useCallback((date, options) => {
        switch (options.type) {
            case 'date': return formatDateOnly(date, options);
            case 'time': return formatTimeOnly(date, options);
            case 'datetime':
            default:
                return formatDateTimeOnly(date, options);
        }
    }, [formatDateOnly, formatTimeOnly, formatDateTimeOnly]);
    return { formatDate };
};
